import React from 'react';
import _ from 'lodash';

import {
  useDeleteListingManagementStrategyMutation,
  useInventoryActivity_ItemDetailDrawerQuery,
  useToggleListingManagementStrategiesForItemMutation
} from 'generated/graphql';

import Big from 'big.js';



export interface IManagementStrategy {
  id: string;
  storeId: string;
  storeName: string;
  priceDollars: Big;
  isInventoryManagementDisabled: boolean;
}
export interface ItemBaselineQuantity {
  baselineId: string;
  locationId: string;
  locationName: string;
  quantity: number;
}
export interface IData {
  sku: string;
  name: string;
  description: string;
  weightValue: string;
  weightUnit: string;
  isActive: boolean;
  defaultPriceDollars: Big;
  defaultWholesalePriceDollars: Big;
  costDollars: Big;
  managementStrategies: IManagementStrategy[];
  quantitiesOnLocations: ItemBaselineQuantity[];
  deviceType: string;
}

export function useQuery(itemId: string) {
  const tuple = useInventoryActivity_ItemDetailDrawerQuery({
    variables: {
      itemId
    }
  });

  return React.useMemo(
    () => {
      const item = tuple.data?.item;
      const data: IData = {
        isActive: _.every(tuple.data?.item?.listingManagementStrategies ?? [], strategy => strategy.isActive),
        costDollars: new Big(tuple.data?.item?.defaultCostDollars ?? 0),
        defaultPriceDollars: new Big(item?.defaultPriceDollars ?? 0),
        defaultWholesalePriceDollars: new Big(item?.defaultWholesalePriceDollars ?? 0),
        name: item?.name ?? '',
        sku: item?.sku ?? '',
        description: item?.description ?? '',
        deviceType: item?.deviceType ?? '',
        weightValue: item?.weightValue.toString() ?? '',
        weightUnit: item?.weightUnit ?? '',
        managementStrategies: _.sortBy(_.map(item?.listingManagementStrategies, (strategy): IManagementStrategy => ({
          id: strategy.id,
          storeId: strategy.store.id,
          storeName: strategy.store.name,
          isInventoryManagementDisabled: strategy.store.isInventoryManagementDisabled,
          priceDollars: new Big(strategy.priceDollars || 0)
        })), listing => listing.storeName),
        quantitiesOnLocations: _.map(item?.itemBaselineQuantities, quantityOnLocation => ({
          baselineId: quantityOnLocation.id,
          locationId: quantityOnLocation?.locationId ?? '',
          locationName: quantityOnLocation?.locationName ?? '',
          quantity: quantityOnLocation.quantity
        }))
      };

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}



export function useDeleteListingManagementStrategy() {
  const [fnMutate, tuple] = useDeleteListingManagementStrategyMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (id: string) => {
      await fnMutate({
        variables: {
          id
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}


export function useToggleListingManagementStrategiesForItem(itemId: string) {
  const [fnMutate, tuple] = useToggleListingManagementStrategiesForItemMutation({
    refetchQueries: []
  });

  const fnWrappedMutate = React.useCallback(
    async (isActive: boolean) => {
      await fnMutate({
        variables: {
          input: {
            itemId,
            isActive
          }
        }
      });
    },
    [
      fnMutate,
      itemId
    ]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
